import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import { useDonorManagement } from '../hooks/useDonorManagement';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectProjects, selectProjectsStatus, selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { getProject, searchProjects } from '../../../Module/Project/Presentation/projectActions';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import { useTranslation } from 'react-i18next';
import { openMailClient } from '../../../Utils/mail';
import { buildSupporterMessageMailTemplate } from '../../../Utils/shareMailTemplates';
import { getProjectShareImageUrl, shareKakaoFeed } from '../../../Utils/kakaoShare';
import { formatUserFullName } from '../../../Utils/formatUserName';
import { formatDisplayCurrency } from '../../../Utils/formatCurrency';
import { PATHS } from '../../../routes/paths';

const DonorManagementMob = () => {
  const { t } = useTranslation('translation');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'complete' | 'fail'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false);
  const [tempStatus, setTempStatus] = useState<'all' | 'complete' | 'fail'>('all');
  const statuses = ['all', 'complete', 'fail'] as const;
  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;
  const userId = (session as any)?.user?.id || (typeof window !== 'undefined' ? window.localStorage.getItem('missionfund.userId') : null) || '';

  useEffect(() => {
    if (projectId) {
      // 선택된 프로젝트가 없거나, 현재 선택된 프로젝트와 파라미터의 projectId가 다른 경우 정보 요청
      if (!selectedProject || selectedProject.project_id !== projectId) {
        dispatch(getProject({ project_id: projectId }));
      }
    }
  }, [dispatch, projectId, selectedProject]);

  useEffect(() => {
    if (!projectId && userId && projects.length === 0 && projectsStatus !== 'loading') {
      dispatch(searchProjects({ limit: 100, params: [{ fld: 'user_id', op: 'eq', val: userId }], sort: 'created_at:desc' }));
    }
  }, [dispatch, projectId, userId, projects.length, projectsStatus]);

  useEffect(() => {
    if (!projectId && projectsStatus === 'succeeded' && projects.length === 0) {
      navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
    }
  }, [projectId, projectsStatus, projects.length, navigate]);

  const currentProject = useMemo(() => {
    if (projectId) {
      if (selectedProject?.project_id === projectId) return selectedProject;
      const found = projects.find((p) => p.project_id === projectId);
      return found ?? selectedProject;
    }
    return selectedProject || projects[0];
  }, [projectId, selectedProject, projects]);
  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');
  const fallbackFullName = formatUserFullName(
    (session as { user?: { first_name?: string } } | undefined)?.user?.first_name,
    (session as { user?: { last_name?: string } } | undefined)?.user?.last_name,
  );
  const userFullName =
    (session as { user?: { name?: string } } | undefined)?.user?.name ??
    (fallbackFullName ? fallbackFullName : undefined) ??
    (session as { user?: { email?: string } } | undefined)?.user?.email ??
    '';
  const senderEmail = ((session as { user?: { email?: string } } | undefined)?.user?.email ?? '').trim();

  const effectiveProjectId = projectId ?? currentProject?.project_id;
  // API 연동 훅 사용
  const { summary, donors, hasData, isLoading, isLoadingMore, hasMore, loadMoreDonors, fetchActiveDonorsAllPages } =
    useDonorManagement(effectiveProjectId);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 무한 스크롤: 하단 감지 시 더 불러오기
  useEffect(() => {
    if (!hasMore || isLoadingMore || isLoading) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreDonors();
      },
      { rootMargin: '400px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, loadMoreDonors]);

  const runKakaoShare = () => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const projectUrl = `https://go.missionfund.org/${effectiveProjectId || ''}`;

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(projectUrl);
      };
      document.head.appendChild(script);
    } else {
      if (!(window as any).Kakao.isInitialized()) {
        (window as any).Kakao.init(KAKAO_KEY);
      }
      shareToKakao(projectUrl);
    }
  };

  const shareToKakao = (url: string) => {
    const kakao = (window as any).Kakao;
    if (!(kakao?.Share?.sendDefault ?? kakao?.Link?.sendDefault)) {
      return;
    }
    const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
    shareKakaoFeed(kakao, {
      url,
      title: projectTitle || t('common.shareModal.defaultTitle'),
      imageUrl: getProjectShareImageUrl(currentProject as { image_url?: string; video_url?: string }),
      buttonTitle: t('common.shareModal.buttonDetail'),
      fundraiserName: userFullName || (currentProject as any)?.user_full_name || (currentProject as any)?.fundraiser_name || t('common.share.fundraiserFallback'),
      restrictedRegion,
    });
  };

  const fundraiserName =
    userFullName ||
    (currentProject as any)?.user_full_name ||
    (currentProject as any)?.fundraiser_name ||
    '';
  const projectUrlForMail = `https://go.missionfund.org/${effectiveProjectId || ''}`;

  const runEmailShare = () => {
    const { subject, body } = buildSupporterMessageMailTemplate({
      fundraiserName,
      projectUrl: projectUrlForMail,
    });
    openMailClient({ subject, body });
  };

  const handleBulkEmail = async () => {
    const fundraiserTo = senderEmail;
    if (!fundraiserTo) {
      alert(t('page.donorManagement.alerts.bulkEmailNeedFundraiserEmail'));
      return;
    }
    const allRows = await fetchActiveDonorsAllPages();
    const donorEmails = Array.from(
      new Set(
        allRows
          .map((donor) => donor.email?.trim())
          .filter((email): email is string => Boolean(email))
      )
    );
    if (donorEmails.length === 0) {
      alert(t('page.donorManagement.alerts.bulkEmailNoDonors'));
      return;
    }
    const cc = donorEmails.filter((e) => e.toLowerCase() !== fundraiserTo.toLowerCase());
    const { subject, body } = buildSupporterMessageMailTemplate({
      fundraiserName,
      projectUrl: projectUrlForMail,
    });
    openMailClient({
      to: fundraiserTo,
      cc: cc.length > 0 ? cc : undefined,
      subject,
      body,
    });
  };

  const handleDonorEmail = (email?: string) => {
    const to = email?.trim();
    if (!to) return;
    const { subject, body } = buildSupporterMessageMailTemplate({
      fundraiserName,
      projectUrl: projectUrlForMail,
    });
    openMailClient({ to, subject, body });
  };

  const handleKakaoShareOption = () => {
    setIsShareModalOpen(false);
    runKakaoShare();
  };

  const handleEmailShareOption = () => {
    setIsShareModalOpen(false);
    runEmailShare();
  };

  const handleCopyLink = async () => {
    const url = `https://go.missionfund.org/${effectiveProjectId || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
      setIsShareModalOpen(false);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
      setIsShareModalOpen(false);
    }
  };

  const formatCurrency = (amount: number, currencyType?: string) => formatDisplayCurrency(amount, currencyType);
  const statusLabel = (status: typeof statuses[number]) => (status === 'all' ? t('common.filters.all') : t(`common.payment.status.${status}`));
  const filterDonorBySearchAndStatus = (donor: { name: string; status: 'complete' | 'fail' | 'cancel' }) => {
    const matchesStatus = selectedStatus === 'all' || donor.status === selectedStatus;
    const trimmed = searchTerm.trim().toLocaleLowerCase();
    const matchesSearch = trimmed.length === 0 || donor.name.toLocaleLowerCase().includes(trimmed);
    return matchesStatus && matchesSearch;
  };
  const filteredDonors = useMemo(() => donors.filter(filterDonorBySearchAndStatus), [donors, searchTerm, selectedStatus]);
  const handleSearch = () => setSearchTerm((prev) => prev.trim());
  const handleStatusClick = () => {
    setTempStatus(selectedStatus);
    setIsStatusPickerOpen(true);
  };
  const handleStatusApply = () => {
    setSelectedStatus(tempStatus);
    setIsStatusPickerOpen(false);
  };

  const handleExcelDownload = async () => {
    if (!donors || donors.length === 0) return;

    const exportRows = await fetchActiveDonorsAllPages();
    const filteredExportRows = exportRows.filter(filterDonorBySearchAndStatus);
    if (filteredExportRows.length === 0) return;

    const headerStyle = {
      fill: { fgColor: { rgb: 'D3D3D3' } },
      font: { bold: false, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const cellStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const rightAlignStyle = {
      alignment: { horizontal: 'right', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const commentStyle = {
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const headers = [
      'No', 'Donor name', 'Phone', 'Email', 'Address',
      'Donation Type', 'Payment', 'Status', 'History',
      'Amount', 'Date', 'Comments',
    ];

    const formatExcelAmount = (amount: number, currencyType?: string) => (
      formatDisplayCurrency(amount, currencyType, { useSymbol: true })
    );

    const data = filteredExportRows.map((d, idx) => ([
      filteredExportRows.length - idx,
      d.name,
      d.phone,
      d.email,
      d.address || '',
      d.donationType,
      d.method.toUpperCase(),
      d.status === 'cancel' ? 'cancelled' : (d.status === 'fail' ? 'failed' : 'active'),
      '',
      formatExcelAmount(d.amount, d.currencyType),
      d.date,
      d.message,
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    ws['!cols'] = [
      { wch: 5 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 80 },
    ];

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        const address = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[address]) ws[address].s = headerStyle;
      }
      for (let r = 1; r <= range.e.r; r += 1) {
        for (let c = range.s.c; c <= range.e.c; c += 1) {
          const address = XLSX.utils.encode_cell({ r, c });
          if (ws[address]) {
            ws[address].s = cellStyle;
            if (c === 9) ws[address].s = rightAlignStyle;
            if (c === 11) ws[address].s = commentStyle;
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateCompact = `${yyyy}${mm}${dd}`;
    const projectIdForFile = String(currentProject?.project_id ?? effectiveProjectId ?? 'project')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    XLSX.utils.book_append_sheet(wb, ws, `Donations_${dateCompact}`);
    const fileName = `donations_${projectIdForFile}_${dateCompact}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  const renderEmptyState = () => (
    <EmptyStateWrapper>
      <EmptyTitleGroup>
        <EmptyTitleBlue>
          {t('common.empty.titleBlueLine1')} <br /> {t('common.empty.titleBlueLine2')}
        </EmptyTitleBlue>
        <EmptyTitleBlack>
          {t('common.empty.titleBlackLine1')} <br /> {t('common.empty.titleBlackLine2')}
        </EmptyTitleBlack>
      </EmptyTitleGroup>
      <ShareBtn onClick={() => setIsShareModalOpen(true)}>
        <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
        {t('common.empty.share')}
      </ShareBtn>
    </EmptyStateWrapper>
  );

  if (isLoading) {
    return (
      <MobilePageLayout title={t('page.donorManagement.pageTitle')} showBottomNav={false}>
        <DSLoading />
      </MobilePageLayout>
    );
  }

  return (
    <MobilePageLayout title={t('page.donorManagement.pageTitle')}>
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="mobile" />
        <PageTitle>{t('page.donorManagement.pageTitle')}</PageTitle>

        {!hasData ? (
          renderEmptyState()
        ) : (
          <>
            <SummaryGrid>
              <SummaryCard variant="yellow">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.totalTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.totalDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.total}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="blue">
                <CardLabelGroup>
                  <CardTitle highlight={true}>{t('page.donorManagement.summary.inProgressTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.inProgressDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue color="blue">{summary.inProgress}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="white">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.failedTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.failedDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.failed}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="white">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.cancelledTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.cancelledDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.cancelled}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>
            </SummaryGrid>

            <ActionSection>
              <ExcelButtonFull onClick={handleExcelDownload}>
                <DownloadIcon />
                {t('common.actions.downloadExcel')}
              </ExcelButtonFull>
              <EmailAllButtonFull onClick={handleBulkEmail}>
                <EmailIcon />
                {t('page.donorManagement.actions.emailBulk')}
              </EmailAllButtonFull>
              <HelperText>{t('page.donorManagement.helper')}</HelperText>
            </ActionSection>

            <FilterSection>
              <FilterInputWrapper>
                <FilterInput
                  placeholder={t('page.paymentStatus.filters.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchIconSmall />
              </FilterInputWrapper>
              <FilterInputWrapper onClick={handleStatusClick}>
                <StatusText isPlaceholder={selectedStatus === 'all'}>
                  {selectedStatus === 'all' ? t('page.paymentStatus.filters.statusSelect') : statusLabel(selectedStatus)}
                </StatusText>
                <ArrowIcon />
              </FilterInputWrapper>
              <SearchButtonMobile onClick={handleSearch}>{t('common.actions.search')}</SearchButtonMobile>
            </FilterSection>

            <DonorList>
              {filteredDonors.map((donor, index) => (
                <DonorItem
                  key={donor.id}
                  isFirst={index === 0}
                  isLast={index === filteredDonors.length - 1}
                  isCancelled={donor.status === 'cancel'}
                >
                  <ProfileSection>
                    <ProfileImg src={donor.profileImg} alt={donor.name} />
                    <ProfileInfo>
                      <DonorName color={donor.status === 'cancel' ? 'gray' : 'black'}>
                        {donor.name}
                      </DonorName>
                      <BadgeGroup>
                        <Badge type={donor.method}>{t(`common.payment.method.${donor.method}`)}</Badge>
                        <Badge type={donor.status}>{t(`common.payment.status.${donor.status}`)}</Badge>
                      </BadgeGroup>
                    </ProfileInfo>
                  </ProfileSection>

                  <DonorContactInfo>
                    {donor.email?.trim() && (
                      <ContactItem>
                        <ContactEmailIcon />
                        {donor.email}
                      </ContactItem>
                    )}
                    <ContactItem>
                      <ContactPhoneIcon />
                      {donor.phone}
                    </ContactItem>
                    <ContactItem>
                      <ContactDateIcon />
                      {donor.date} {t('common.labels.registered')}
                    </ContactItem>
                  </DonorContactInfo>

                  {donor.message ? <DonorMessage>{donor.message}</DonorMessage> : null}

                  <AmountSectionMob>
                    <AmountBoxMob>
                      <AmountLabel>{t('page.donorManagement.labels.amount')}</AmountLabel>
                      <AmountValue>{formatCurrency(donor.amount, donor.currencyType)}</AmountValue>
                    </AmountBoxMob>
                  </AmountSectionMob>

                  {donor.email?.trim() && (
                  <EmailButtonMob type="button" onClick={() => handleDonorEmail(donor.email)}>
                    <EmailIconSmallBlue />
                    {t('page.donorManagement.actions.sendEmail')}
                  </EmailButtonMob>
                  )}
                </DonorItem>
              ))}
              {filteredDonors.length === 0 ? (
                <NoResultsMobile>
                  <NoResultsTextMobile>{t('page.paymentStatus.details.noResults')}</NoResultsTextMobile>
                </NoResultsMobile>
              ) : null}
              {hasMore && <div ref={loadMoreRef} style={{ height: 1, minHeight: 1 }} aria-hidden />}
              {isLoadingMore && (
                <LoadingMoreRow>
                  <DSLoading variant="content" />
                </LoadingMoreRow>
              )}
            </DonorList>
          </>
        )}
      </Container>

      <DSBottomSheet open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} padding="0">
        <ShareBottomSheetWrapper>
          <ShareHeader>
            <TitleRow>
              <ShareTitle>{t('page.donorManagement.shareModal.title')}</ShareTitle>
              <CloseButton onClick={() => setIsShareModalOpen(false)} aria-label="close">
                ✕
              </CloseButton>
            </TitleRow>
            <ShareDescription>
              {t('common.shareModal.descLine1')}
              <br />
              {t('common.shareModal.descLine2')}
            </ShareDescription>
          </ShareHeader>

          <ShareOptionsContainer>
            <ShareOption onClick={handleKakaoShareOption}>
              <KakaoIconBox>
                <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
              </KakaoIconBox>
              <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
            </ShareOption>

            <ShareOption onClick={handleEmailShareOption}>
              <EmailIconBox>
                <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
              </EmailIconBox>
              <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
            </ShareOption>
          </ShareOptionsContainer>

          <CopyLinkButtonContainer>
            <CopyLinkButton onClick={handleCopyLink}>
              <CopyIcon />
              {t('common.shareModal.copyLink')}
            </CopyLinkButton>
          </CopyLinkButtonContainer>
        </ShareBottomSheetWrapper>
      </DSBottomSheet>
      <DSBottomSheet
        open={isStatusPickerOpen}
        title={t('page.paymentStatus.filters.statusSelect')}
        onClose={() => setIsStatusPickerOpen(false)}
      >
        <StyledBottomSheetContent>
          {statuses.map((status) => (
            <RadioOptionItem
              key={status}
              active={tempStatus === status}
              onClick={() => setTempStatus(status)}
            >
              <RadioCircle active={tempStatus === status} />
              <RadioLabel active={tempStatus === status}>{statusLabel(status)}</RadioLabel>
            </RadioOptionItem>
          ))}
        </StyledBottomSheetContent>
        <BottomSheetButton onClick={handleStatusApply}>
          {t('common.actions.selectComplete')}
        </BottomSheetButton>
      </DSBottomSheet>
    </MobilePageLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin-bottom: ${convertToVwMob(20)};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${convertToVwMob(8)};
  margin-bottom: ${convertToVwMob(20)};
`;

const SummaryCard = styled.div<{ variant: 'yellow' | 'white' | 'blue' }>`
  min-width: 0;
  height: ${convertToVwMob(163)};
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(10)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  
  background: ${({ theme, variant }) => {
    if (variant === 'yellow') return theme.colors.yellow[5] || '#fffaea';
    if (variant === 'blue') return theme.colors.primaryBlue[5] || '#f8fdff';
    return 'white';
  }};

  ${({ theme, variant }) => variant === 'yellow' && `border: 1.5px solid ${theme.colors.yellow[50] || '#ffe390'};`}
  ${({ theme, variant }) => variant === 'blue' && `border: 1.5px solid ${theme.colors.primaryBlue[40] || '#2bc2e1'};`}
`;

const CardLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const CardTitle = styled.span<{ highlight?: boolean }>`
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme, highlight }) => highlight ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const CardDesc = styled.span`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const CardValue = styled.span<{ color?: 'blue' }>`
  font-size: ${convertToVwMob(28)};
  font-weight: 500;
  color: ${({ theme, color }) => color === 'blue' ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  margin-bottom: ${convertToVwMob(20)};
`;

const FilterSection = styled.div`
  display: flex;
  gap: ${convertToVwMob(8)};
  margin-bottom: ${convertToVwMob(20)};
`;

const FilterInputWrapper = styled.div`
  flex: 1;
  height: ${convertToVwMob(52)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(4)};
  display: flex;
  align-items: center;
  padding: 0 ${convertToVwMob(12)};
  gap: ${convertToVwMob(8)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const FilterInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  background-color: transparent;
  min-width: 0;
  &::placeholder {
    color: #929292;
    font-family: Pretendard;
    font-size: ${convertToVwMob(24)};
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }
`;

const SearchIconSmall = styled.div`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M21 21L16.65 16.65' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const StatusText = styled.span<{ isPlaceholder?: boolean }>`
  flex: 1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme, isPlaceholder }) => (isPlaceholder ? '#929292' : theme.colors.grayScale[90])};
`;

const ArrowIcon = styled.div`
  width: ${convertToVwMob(14)};
  height: ${convertToVwMob(14)};
  border-left: 2px solid ${({ theme }) => theme.colors.grayScale[60]};
  border-bottom: 2px solid ${({ theme }) => theme.colors.grayScale[60]};
  transform: rotate(-45deg);
  margin-top: -${convertToVwMob(2)};
`;

const SearchButtonMobile = styled.button`
  width: ${convertToVwMob(90)};
  height: ${convertToVwMob(52)};
  border: none;
  border-radius: ${convertToVwMob(8)};
  background: ${({ theme }) => theme.colors.primaryBlue[80]};
  color: #fff;
  font-size: ${convertToVwMob(18)};
  font-weight: 600;
`;

const ExcelButtonFull = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  background: ${({ theme }) => theme.colors.green[65] || '#21a366'};
  border: none;
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: white;
  cursor: pointer;
`;

const EmailAllButtonFull = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  cursor: pointer;
  transition: background 0.15s;

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
  }
`;

const HelperText = styled.p`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const LoadingMoreRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  min-height: 80px;
`;

const NoResultsMobile = styled.div`
  min-height: ${convertToVwMob(220)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoResultsTextMobile = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  margin: 0;
`;

const DonorList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(12)};
  overflow: hidden;
`;

const DonorItem = styled.div<{ isFirst: boolean; isLast: boolean; isCancelled?: boolean }>`
  padding: ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  background: ${({ isCancelled, theme }) => isCancelled ? (theme.colors.grayScale[10] || '#f0f1f2') : 'white'};
  border-bottom: ${({ isLast, theme }) => isLast ? 'none' : `1px solid ${theme.colors.grayScale[20]}`};
`;

const ProfileSection = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
`;

const ProfileImg = styled.img`
  width: ${convertToVwMob(84)};
  height: ${convertToVwMob(84)};
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const DonorName = styled.span<{ color: 'black' | 'gray' }>`
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme, color }) => color === 'gray' ? theme.colors.grayScale[70] : theme.colors.grayScale[90]};
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(4)};
`;

const Badge = styled.span<{ type: 'cms' | 'card' | 'complete' | 'fail' | 'cancel' }>`
  padding: ${convertToVwMob(4)} ${convertToVwMob(10)};
  border-radius: ${convertToVwMob(4)};
  font-size: ${convertToVwMob(12)};
  ${({ theme, type }) => {
    switch (type) {
      case 'cms': return `background: ${theme.colors.primaryBlue[80] || '#026478'}; color: white;`;
      case 'card': return `background: ${theme.colors.primaryBlue[10] || '#ebfcff'}; color: ${theme.colors.primaryBlue[50] || '#25b9d8'};`;
      case 'complete': return `background: ${theme.colors.primaryBlue[20] || '#b7f2ff'}; color: ${theme.colors.grayScale[90]};`;
      case 'fail': return `background: white; border: 1px solid ${theme.colors.other?.red5 || '#ffc0c0'}; color: ${theme.colors.other?.red10 || '#ff0000'};`;
      case 'cancel': return `background: ${theme.colors.grayScale[30] || '#e5e5e5'}; color: ${theme.colors.grayScale[50] || '#bcbcbc'};`;
    }
  }}
`;

const DonorContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const ContactItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const ContactEmailIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3333 2.66666H2.66666C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V12C1.33333 12.7333 1.93333 13.3333 2.66666 13.3333H13.3333C14.0667 13.3333 14.6667 12.7333 14.6667 12V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 5.33332L8 8.66666L2.66666 5.33332V3.99999L8 7.33332L13.3333 3.99999V5.33332Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactPhoneIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.24 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactDateIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V8H19V19Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const DonorMessage = styled.p`
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  line-height: 1.5;
  margin: 0;
  min-height: ${convertToVwMob(48)};
  display: flex;
  align-items: center;
`;

const AmountSectionMob = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const AmountBoxMob = styled.div`
  padding: ${convertToVwMob(10)} ${convertToVwMob(20)};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(10)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const AmountLabel = styled.span`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const AmountValue = styled.span`
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const EmailButtonMob = styled.button`
  width: 100%;
  height: ${convertToVwMob(48)};
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 1px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  cursor: pointer;
  transition: background 0.15s;

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
  }
`;

const EmailIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z' fill='%232BC2E1'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmailIconSmallBlue = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z' fill='%232BC2E1'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DownloadIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z' fill='white'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmptyStateWrapper = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 20px;
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(29)};
  width: 100%;
  box-sizing: border-box;
`;

const EmptyTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: 0;
`;

const EmptyTitleBlue = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  line-height: 1.5;
`;

const EmptyTitleBlack = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.5;
`;

const ShareBtn = styled.button`
  background: #026478;
  border: none;
  border-radius: 50px;
  width: 100%;
  padding: ${convertToVwMob(14)} ${convertToVwMob(24)};
  min-width: ${convertToVwMob(240)};
  height: ${convertToVwMob(58)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  color: white;
  white-space: nowrap;

  &:focus,
  &:active {
    border-radius: 50px;
    background: var(--PrimaryBlue-90, #416777);
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const ShareIcon = styled.img`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  object-fit: contain;
  aspect-ratio: 1/1;
`;

const ShareBottomSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${convertToVwMob(20)};
`;

const ShareHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${convertToVwMob(24)};
  margin-bottom: ${convertToVwMob(24)};
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${convertToVwMob(12)};
`;

const ShareTitle = styled.h2`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.head.semiBold24}
  margin: 0;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  font-size: ${convertToVwMob(36)};
  font-weight: 300;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(44)};
  height: ${convertToVwMob(44)};
  line-height: 1;
  cursor: pointer;
`;

const ShareDescription = styled.p`
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular20}
  margin: 0;
  padding: 0;
  text-align: left;
`;

const ShareOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(20)};
  padding: 0 ${convertToVwMob(24)};
`;

const ShareOption = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
`;

const KakaoIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
`;

const EmailIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-align: left;
  flex: 1;
`;

const CopyLinkButtonContainer = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(30)};
  background: #F8F8F8;
`;

const CopyLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  width: 100%;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid #929292;
  border-radius: ${convertToVwMob(10)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.medium20}
  cursor: pointer;
`;

const CopyIcon = styled.div`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const StyledBottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  margin-bottom: ${convertToVwMob(16)};
`;

const RadioOptionItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
  padding: ${convertToVwMob(6)} 0;
`;

const RadioCircle = styled.div<{ active: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border: 2px solid ${({ active, theme }) => (active ? theme.colors.primaryBlue[40] : '#bcbcbc')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    width: ${convertToVwMob(12)};
    height: ${convertToVwMob(12)};
    border-radius: 50%;
    background: ${({ active, theme }) => (active ? theme.colors.primaryBlue[40] : 'transparent')};
  }
`;

const RadioLabel = styled.span<{ active: boolean }>`
  font-size: ${convertToVwMob(20)};
  color: ${({ active }) => (active ? '#17171B' : '#929292')};
  font-weight: ${({ active }) => (active ? 500 : 400)};
  line-height: 1.5;
`;

const BottomSheetButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  background: #026478;
  color: white;
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(18)};
  font-weight: 500;
  cursor: pointer;
`;

export default DonorManagementMob;
